import { useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Sidebar from "@/pages/sidebar/sidebar";
import { LimitedAccountWarning } from "@/components/limited-account-warning";
import { MerchantProcessingBanner } from "@/components/merchant-processing-banner";
import { Button } from "@/components/ui/button-tw";
import { ArrowLeft } from "lucide-react";
import { useClientSettings } from "@/queries/client-settings";
import { useAuth } from "@/providers/auth";
import { useTenancy } from "@/hooks/tenancy/use-tenancy";
import {
  QuickPayLinkSection,
  DefaultInvoiceInfoSection,
  BrandingSection,
  NotificationsSection,
  InvoicePreviewPanel,
  MerchantProcessingSection,
} from "@/components/invoices/invoice-settings";

interface BusinessInfo {
  businessName: string;
  businessEmail: string;
  businessAddress: string;
  businessAddressLine2: string;
  businessCity: string;
  businessState: string;
  businessZip: string;
  hideAddress: boolean;
}

interface Branding {
  logo: string;
  brandColor: string;
  secondaryColor: string;
}

const InvoiceSettings = () => {
  const history = useHistory();
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;
  const isAdmin = userRole === "admin";

  const [currentWidth, setCurrentWidth] = useState(0);
  const [activeTab, setActiveTab] = useState<"invoice" | "merchant">("invoice");
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
    businessName: "",
    businessEmail: "",
    businessAddress: "",
    businessAddressLine2: "",
    businessCity: "",
    businessState: "",
    businessZip: "",
    hideAddress: false,
  });
  const [branding, setBranding] = useState<Branding>({
    logo: "",
    brandColor: "#000000",
    secondaryColor: "#000000",
  });
  const [logoPreview, setLogoPreview] = useState<string>("");

  const { data: tenancy } = useTenancy();
  const showMerchantProcessing = !!(tenancy as any)?.feature_flags?.credit_card_payment;

  const { data: clientSettings } = useClientSettings({
    userId: userId || "",
    category: "branding",
  });

  const handleBusinessInfoUpdate = useCallback((data: BusinessInfo) => {
    setBusinessInfo(data);
  }, []);

  const handleBrandingUpdate = useCallback((data: Branding) => {
    setBranding(data);
  }, []);

  const handleLogoPreviewUpdate = useCallback((preview: string) => {
    setLogoPreview(preview);
  }, []);

  const initialData = useMemo(() => {
    if (!clientSettings) return null;

    const settingsObj: Record<string, any> = {};

    clientSettings.forEach((setting) => {
      if (setting.category === "BRANDING") {
        switch (setting.key) {
          case "BUSINESS_NAME":
            settingsObj.business_name = setting.value?.business_name;
            break;
          case "BUSINESS_EMAIL":
            settingsObj.business_email = setting.value?.business_email;
            break;
          case "BUSINESS_ADDRESS":
            settingsObj.business_address = setting.value?.business_address;
            break;
          case "BUSINESS_ADDRESS_2":
            settingsObj.business_address_line2 =
              setting.value?.business_address_line2;
            break;
          case "BUSINESS_CITY":
            settingsObj.business_city = setting.value?.business_city;
            break;
          case "BUSINESS_STATE":
            settingsObj.business_state = setting.value?.business_state;
            break;
          case "BUSINESS_ZIP":
            settingsObj.business_zip = setting.value?.business_zip;
            break;
          case "HIDE_ADDRESS":
            settingsObj.hide_address = setting.value?.hide_address;
            break;
          case "PRIMARY_COLOR":
            settingsObj.primary_color = setting.value?.color;
            break;
          case "SECONDARY_COLOR":
            settingsObj.secondary_color = setting.value?.color;
            break;
          case "LOGO_URL":
            settingsObj.logo_url = setting.value?.url;
            break;
        }
      }
    });

    if (Object.keys(settingsObj).length === 0) return null;

    const normalizeHexColor = (color: string): string => {
      if (!color) return "#000000";
      let hex = color.replace("#", "");
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((char) => char + char)
          .join("");
      }
      if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
        return "#000000";
      }
      return "#" + hex;
    };

    return {
      businessInfo: {
        businessName: settingsObj.business_name || "",
        businessEmail: settingsObj.business_email || "",
        businessAddress: settingsObj.business_address || "",
        businessAddressLine2: settingsObj.business_address_line2 || "",
        businessCity: settingsObj.business_city || "",
        businessState: settingsObj.business_state || "",
        businessZip: settingsObj.business_zip || "",
        hideAddress: settingsObj.hide_address || false,
      },
      branding: {
        logo: settingsObj.logo_url || "",
        brandColor: normalizeHexColor(settingsObj.primary_color || "#000000"),
        secondaryColor: normalizeHexColor(
          settingsObj.secondary_color || "#000000"
        ),
      },
    };
  }, [clientSettings]);

  return (
    <div className="min-h-full">
      <Sidebar currentWidth={currentWidth} setCurrentWidth={setCurrentWidth} />

      <LimitedAccountWarning />
      <MerchantProcessingBanner />
      <div className="relative lg:ml-80 h-full bg-gray-50 px-0 md:px-3 pt-4 md:pt-6 xl:pt-8 pb-12 xl:px-6 2xl:px-8 lg:pb-32">
        <div className="max-w-[1720px] mx-auto">
          {/* Header */}
          <div className="flex items-center mb-4 md:mb-6 px-4 md:px-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => history.push("/invoices")}
              className="mr-2"
            >
              <ArrowLeft size={20} />
            </Button>
            <span className="text-xl md:text-3xl font-medium text-gray-900">
              Invoice Settings
            </span>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6 px-4 md:px-0">
            <button
              onClick={() => setActiveTab("invoice")}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === "invoice"
                  ? "border-gray-900 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Invoice &amp; Quick Pay Settings
            </button>
            {showMerchantProcessing && (
              <button
                onClick={() => setActiveTab("merchant")}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === "merchant"
                    ? "border-gray-900 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Merchant Processing Settings
              </button>
            )}
          </div>

          {activeTab === "invoice" ? (
            /* Main Content Grid */
            <div className="mx-auto grid grid-cols-5 gap-0 md:gap-6 items-start">
              {/* Left Column - Settings Forms */}
              <main className="flex-1 col-span-5 lg:col-span-3">
                <div className="space-y-4 px-4 md:px-0">
                  <QuickPayLinkSection userId={userId || ""} isAdmin={isAdmin} />

                  <DefaultInvoiceInfoSection
                    userId={userId || ""}
                    isAdmin={isAdmin}
                    initialData={initialData?.businessInfo || null}
                    onUpdate={handleBusinessInfoUpdate}
                  />

                  <BrandingSection
                    userId={userId || ""}
                    isAdmin={isAdmin}
                    initialData={initialData?.branding || null}
                    onUpdate={handleBrandingUpdate}
                    onLogoPreviewUpdate={handleLogoPreviewUpdate}
                  />

                  <NotificationsSection
                    userId={userId || ""}
                    isAdmin={isAdmin}
                  />
                </div>
              </main>

              {/* Right Column - Invoice Preview */}
              <InvoicePreviewPanel
                businessInfo={businessInfo}
                branding={branding}
                logoPreview={logoPreview}
              />
            </div>
          ) : showMerchantProcessing ? (
            <div className="px-4 md:px-0 max-w-2xl">
              <MerchantProcessingSection userRole={userRole} userId={userId || ""} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default InvoiceSettings;
